// https://github.com/tastyworks/tastyworks-api-ember/blob/d63660ed93cc9dd102391f3b7d4745b975387e68/addon/models/access-plan.js#L102
import type { FormDataAdapter } from "$lib/util/model-adapters"
import { PatchBuilder } from "$lib/util/model-adapters/patch.ts"
import {
  type AccountOpeningApplication,
  OPTIONS_LEVEL,
  ValidateYup,
} from "@tastyworks/tastyworks-api"
import { type InferType, mixed, object } from "yup"

export const BASIC_PLAN_LEVEL = "DEFINED_RISK" as OPTIONS_LEVEL

export type Plan = {
  optionsLevel: OPTIONS_LEVEL
  permission: Permission
  restricted: boolean
}

export enum Permission {
  BUY_STOCK = 1 << 0,
  BUY_OPTIONS = 1 << 1,
  SELL_COVERED_CALLS = 1 << 2,
  CRYPTOCURRENCY = 1 << 3,
  DEFINED_RISK_SPREAD = 1 << 4,
  SELL_NAKED_PUTS = 1 << 5,
  SELL_NAKED_CALLS = 1 << 6,
  SELL_STOCK = 1 << 7,
  FUTURES = 1 << 8,
  SELL_CASH_SECURED_PUTS = 1 << 9,
}

export enum PermissionIra {
  BUY_STOCK = 1 << 0,
  BUY_OPTIONS = 1 << 1,
  SELL_COVERED_CALLS = 1 << 2,
  SELL_CASH_SECURED_PUTS = 1 << 3,
  DEFINED_RISK_SPREAD = 1 << 4,
  SELL_NAKED_CALLS = 1 << 5,
  FUTURES = 1 << 6,
  SELL_STOCK = 1 << 7,
  SELL_NAKED_PUTS = 1 << 8,
}

const THE_WORKS_PLAN: Plan = {
  optionsLevel: OPTIONS_LEVEL.NO_RESTRICTIONS,
  permission:
    Permission.BUY_STOCK |
    Permission.BUY_OPTIONS |
    Permission.SELL_COVERED_CALLS |
    Permission.CRYPTOCURRENCY |
    Permission.DEFINED_RISK_SPREAD |
    Permission.SELL_NAKED_PUTS |
    Permission.SELL_NAKED_CALLS |
    Permission.SELL_STOCK |
    Permission.FUTURES,
  restricted: false,
}

const BASIC_PLAN: Plan = {
  optionsLevel: OPTIONS_LEVEL.DEFINED_RISK,
  permission:
    Permission.BUY_STOCK |
    Permission.BUY_OPTIONS |
    Permission.SELL_COVERED_CALLS |
    Permission.CRYPTOCURRENCY |
    Permission.DEFINED_RISK_SPREAD |
    Permission.SELL_NAKED_PUTS,
  restricted: false,
}

const LIMITED_PLAN: Plan = {
  optionsLevel: OPTIONS_LEVEL.COVERED_AND_CASH_SECURED,
  permission:
    Permission.BUY_STOCK |
    Permission.BUY_OPTIONS |
    Permission.SELL_COVERED_CALLS |
    Permission.CRYPTOCURRENCY |
    Permission.SELL_CASH_SECURED_PUTS,
  restricted: false,
}

const IRA_BASIC_PLAN: Plan = {
  ...BASIC_PLAN,
  permission:
    PermissionIra.BUY_STOCK |
    PermissionIra.BUY_OPTIONS |
    PermissionIra.SELL_COVERED_CALLS |
    PermissionIra.DEFINED_RISK_SPREAD |
    PermissionIra.SELL_CASH_SECURED_PUTS,
}

const IRA_LIMITED_PLAN: Plan = {
  ...LIMITED_PLAN,
  permission:
    PermissionIra.BUY_STOCK |
    PermissionIra.BUY_OPTIONS |
    PermissionIra.SELL_COVERED_CALLS |
    PermissionIra.SELL_CASH_SECURED_PUTS,
}

export const tradingAccountOptionLevelSchema = ValidateYup.buildSchema(
  "TradingAccountOptionLevel",
  object({
    optionLevel: mixed<OPTIONS_LEVEL>()
      .required()
      .oneOf(Object.values(OPTIONS_LEVEL)),
  })
)

export const tradingAccountOptionLevelAdapter = {
  toForm: (application, formData) => {
    formData.optionLevel = application.optionsLevel ?? undefined
    return formData
  },
  toPatch: (data) =>
    new PatchBuilder<AccountOpeningApplication>()
      .string("optionsLevel", data.optionLevel)
      .build(),
} satisfies Pick<
  FormDataAdapter<
    AccountOpeningApplication,
    InferType<typeof tradingAccountOptionLevelSchema>
  >,
  "toForm" | "toPatch"
>

export const getTradingOptionLevels = function (
  permittedOptionsLevel: OPTIONS_LEVEL,
  isRetirementAccountType: boolean,
  isCash: boolean
) {
  let plans = [THE_WORKS_PLAN, BASIC_PLAN, LIMITED_PLAN]
  if (isRetirementAccountType) {
    plans = [IRA_BASIC_PLAN, IRA_LIMITED_PLAN]
    return plans
  }
  const permittedPlanIndex = plans.indexOf(
    plans.find((x) => x.optionsLevel === permittedOptionsLevel)!
  )

  plans.forEach((p, i) => {
    let restricted = true
    if (isCash && p.optionsLevel != OPTIONS_LEVEL.COVERED_AND_CASH_SECURED) {
      restricted = true
    } else if (planIsLessThan(i, permittedPlanIndex)) {
      restricted = false
    }

    p.restricted = restricted
  })

  return plans
}

function planIsLessThan(optionLevelIndex: number, maxPlanIndex: number) {
  return optionLevelIndex >= maxPlanIndex
}

if (import.meta.vitest) {
  const { it, expect, describe } = import.meta.vitest

  describe("tradingAccountOptionLevelSchema", () => {
    it("should validate correctly for a valid OPTIONS_LEVEL", async () => {
      const tradingAccountOptionLevel = {
        optionLevel: OPTIONS_LEVEL.NOT_SUITABLE,
      }

      const result = await tradingAccountOptionLevelSchema.validate(
        tradingAccountOptionLevel
      )
      expect(result).toEqual(tradingAccountOptionLevel)
    })

    it("should validate correctly for an invalid optionsLevel", async () => {
      const tradingAccountOptionLevel = {
        optionLevel: "ABC",
      }

      await expect(() =>
        tradingAccountOptionLevelSchema.validate(tradingAccountOptionLevel)
      ).rejects.toThrow()
    })

    it("should validate correctly for a missing optionsLevel", async () => {
      const tradingAccountOptionLevel = {
        optionLevel: undefined,
      }

      await expect(() =>
        tradingAccountOptionLevelSchema.validate(tradingAccountOptionLevel)
      ).rejects.toThrow()
    })
  })

  describe("getTradingOptionLevels", () => {
    it("returns retirement accounts when isRetirementAccountType is true", () => {
      expect(
        getTradingOptionLevels(OPTIONS_LEVEL.NO_RESTRICTIONS, true, true)
      ).toEqual([IRA_BASIC_PLAN, IRA_LIMITED_PLAN])
    })

    it("correctly returns the plan for the provided options level when isCash is false", () => {
      expect(
        getTradingOptionLevels(OPTIONS_LEVEL.NO_RESTRICTIONS, false, false)
      ).toEqual([THE_WORKS_PLAN, BASIC_PLAN, LIMITED_PLAN])
    })

    it("correctly returns the plan for the provided options level when isCash is true", () => {
      expect(
        getTradingOptionLevels(
          OPTIONS_LEVEL.COVERED_AND_CASH_SECURED,
          false,
          true
        )
      ).toEqual([THE_WORKS_PLAN, BASIC_PLAN, LIMITED_PLAN])
    })

    it("correctly returns the plan for the provided options level when isCash is false", () => {
      expect(
        getTradingOptionLevels(
          OPTIONS_LEVEL.COVERED_AND_CASH_SECURED,
          false,
          false
        )
      ).toEqual([THE_WORKS_PLAN, BASIC_PLAN, LIMITED_PLAN])
    })
  })

  describe("tradingAccountOptionLevelAdapter", () => {
    it("fills the form from the application", async () => {
      const { accountOpeningApplicationFactory } =
        await import("@tastyworks/tastyworks-api/factories/account-opening-application")
      const application = accountOpeningApplicationFactory.build({
        optionsLevel: OPTIONS_LEVEL.COVERED_AND_CASH_SECURED,
      })

      expect(tradingAccountOptionLevelAdapter.toForm(application, {})).toEqual({
        optionLevel: OPTIONS_LEVEL.COVERED_AND_CASH_SECURED,
      })
    })

    it("patches the options level", () => {
      expect(
        tradingAccountOptionLevelAdapter.toPatch({
          optionLevel: OPTIONS_LEVEL.COVERED_AND_CASH_SECURED,
        })
      ).toEqual({ optionsLevel: OPTIONS_LEVEL.COVERED_AND_CASH_SECURED })
    })
  })
}
